import { useMemo, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, ArrowRight, RotateCcw } from "lucide-react";
import {
  IMPORT_FIELDS, suggestMapping, coerceImportValue, normalizeApe, slugify,
  CONTRACT_TYPES, type ImportCommitInput,
} from "@avestate/shared";
import { parseCsv, type ParsedCsv } from "@/lib/csv.js";
import { useImportCommit } from "@/hooks/useImport.js";

const MAX_ROWS = 200;
const FIELD_BY_KEY = new Map(IMPORT_FIELDS.map((f) => [f.key, f]));
type Defaults = { contract: (typeof CONTRACT_TYPES)[number]; kind: "immobile" | "cantiere" };
type RowLevel = "ok" | "warn" | "error";

function recordFor(dataRow: string[], mapping: Record<string, number | null>): Record<string, string> {
  const rec: Record<string, string> = {};
  for (const f of IMPORT_FIELDS) {
    const ci = mapping[f.key];
    if (ci == null) continue;
    const v = (dataRow[ci] ?? "").trim();
    if (v) rec[f.key] = v;
  }
  return rec;
}

function previewRow(rec: Record<string, string>): { slug: string | null; title: string | null; level: RowLevel; notes: string[] } {
  const notes: string[] = [];
  let titleIt: string | null = null;
  for (const [k, v] of Object.entries(rec)) {
    const field = FIELD_BY_KEY.get(k);
    if (!field) continue;
    if (k === "ape") { const a = normalizeApe(v); if (a.warning) notes.push(a.warning); continue; }
    const res = coerceImportValue(field, v);
    if (!res.ok) { notes.push(`${field.label}: ${res.error}`); continue; }
    if (res.warning) notes.push(res.warning);
    if (k === "title" && res.value) titleIt = (res.value as { it: string }).it;
  }
  const slug = rec.slug ? slugify(rec.slug) : slugify(titleIt || rec.city || "");
  if (!slug) return { slug: null, title: titleIt, level: "error", notes: ["manca titolo/slug per generare l'URL"] };
  return { slug, title: titleIt, level: notes.length ? "warn" : "ok", notes };
}

export default function ImportListings(): JSX.Element {
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [mapping, setMapping] = useState<Record<string, number | null>>({});
  const [defaults, setDefaults] = useState<Defaults>({ contract: "vendita", kind: "immobile" });
  const [dragOver, setDragOver] = useState(false);
  const commit = useImportCommit();

  async function ingest(file: File): Promise<void> {
    const text = await file.text();
    const parsed = parseCsv(text);
    setCsv(parsed);
    setFileName(file.name);
    setMapping(suggestMapping(parsed.columns));
    commit.reset();
  }

  function onFile(e: ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0];
    if (f) void ingest(f);
  }
  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void ingest(f);
  }

  const records = useMemo(
    () => (csv ? csv.rows.map((r, i) => ({ rec: recordFor(r, mapping), line: csv.lines[i] ?? i + 2 })).filter((x) => Object.keys(x.rec).length > 0) : []),
    [csv, mapping],
  );
  const previews = useMemo(() => records.map((x) => previewRow(x.rec)), [records]);
  const dupColumns = useMemo(() => {
    const counts = new Map<number, string[]>();
    for (const f of IMPORT_FIELDS) {
      const ci = mapping[f.key];
      if (ci != null) counts.set(ci, [...(counts.get(ci) ?? []), f.label]);
    }
    return [...counts.values()].filter((labels) => labels.length > 1);
  }, [mapping]);
  const mappedCount = useMemo(() => Object.values(mapping).filter((v) => v != null).length, [mapping]);
  const canDerive = mapping.title != null || mapping.slug != null || mapping.city != null;
  // Rows actually sent = non-error, capped. The button + stat both derive from this.
  const importable = useMemo(() => records.filter((_, i) => previews[i]!.level !== "error").slice(0, MAX_ROWS), [records, previews]);
  const okCount = importable.length;
  const errCount = previews.filter((p) => p.level === "error").length;
  const eligibleCount = previews.filter((p) => p.level !== "error").length;
  const capped = eligibleCount > MAX_ROWS;

  function reset(): void { setCsv(null); setFileName(""); setMapping({}); commit.reset(); }

  function doImport(): void {
    const body: ImportCommitInput = { rows: importable.map((x) => x.rec), defaults };
    commit.mutate(body);
  }

  // ---- empty state ---------------------------------------------------------
  if (!csv) {
    return (
      <div className="space-y-[22px]">
        <Header />
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          role="group"
          aria-label="Carica un file CSV: trascinalo qui o usa il pulsante Scegli file"
          className={`ticks relative grid place-items-center rounded-[5px] border border-dashed bg-surface p-[56px_24px] text-center transition-colors ${dragOver ? "border-accent bg-accent/5" : "border-rule"}`}
        >
          <Upload className="size-7 text-ink-mute" />
          <p className="mt-3 text-[14px] font-semibold text-ink">Trascina qui il CSV, o</p>
          <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-pill bg-accent px-4 py-2 text-[13px] font-semibold text-accent-on hover:opacity-90">
            <FileSpreadsheet className="size-4" /> Scegli file
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          </label>
          <p className="micro mt-4 max-w-[46ch]">
            Esporta gli immobili dal tuo gestionale, portale o Excel in CSV. Delimitatore , o ; — rileviamo le colonne in automatico. Solo dati testuali: le foto si caricano dopo nella scheda.
          </p>
        </div>
      </div>
    );
  }

  // ---- result state --------------------------------------------------------
  if (commit.isSuccess && commit.data) {
    const d = commit.data;
    return (
      <div className="space-y-[22px]">
        <Header />
        <div className="grid grid-cols-3 overflow-hidden rounded-[5px] border border-rule bg-surface">
          <Stat v={String(d.created)} l="Creati" />
          <Stat v={String(d.skipped)} l="Saltati" />
          <Stat v={String(d.errors)} l="Errori" last />
        </div>
        <div className="ticks relative rounded-[5px] border border-rule bg-surface">
          <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
            <CheckCircle2 className="size-4 text-accent" />
            <h3 className="text-[13px] font-semibold">Esito importazione</h3>
            <Link to="/listings" className="micro ml-auto text-accent hover:underline">vai agli immobili →</Link>
          </div>
          <ul className="max-h-[420px] divide-y divide-rule-soft overflow-y-auto">
            {d.results.map((rrow) => (
              <li key={rrow.row} className="flex items-center gap-3 p-[10px_16px] text-[12.5px]">
                <span className="font-mono text-[11px] text-ink-mute">riga {importable[rrow.row - 1]?.line ?? rrow.row}</span>
                <RowBadge level={rrow.status === "created" ? "ok" : rrow.status === "skipped" ? "warn" : "error"} />
                <span className="font-medium text-ink">{rrow.title || rrow.slug || "—"}</span>
                {rrow.message ? <span className="truncate text-ink-soft">· {rrow.message}</span> : null}
              </li>
            ))}
          </ul>
        </div>
        {errCount > 0 ? <p className="micro">{errCount} righe con errori in anteprima sono state escluse dall'import.</p> : null}
        <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-[4px] border border-rule bg-surface-2 px-3 py-2 text-[13px] text-ink-soft hover:border-accent hover:text-ink">
          <RotateCcw className="size-4" /> Importa un altro file
        </button>
      </div>
    );
  }

  // ---- mapping + preview state --------------------------------------------
  return (
    <div className="space-y-[22px]">
      <Header />

      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
        <Stat v={String(records.length)} l="Righe" />
        <Stat v={String(csv.columns.length)} l="Colonne" />
        <Stat v={String(mappedCount)} l="Campi mappati" />
        <Stat v={String(okCount)} l="Pronti" last />
      </div>

      {/* mapping */}
      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
          <FileSpreadsheet className="size-4 text-accent" />
          <h3 className="text-[13px] font-semibold">Abbina le colonne</h3>
          <span className="micro ml-auto max-w-[30ch] truncate">{fileName}</span>
        </div>
        <div className="grid gap-x-6 gap-y-[10px] p-[16px_18px] sm:grid-cols-2">
          {IMPORT_FIELDS.map((f) => (
            <label key={f.key} className="flex items-center gap-3 text-[13px]">
              <span className="w-[150px] shrink-0 text-ink-soft">{f.label}</span>
              <select
                value={mapping[f.key] ?? ""}
                onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value === "" ? null : Number(e.target.value) }))}
                className="min-w-0 flex-1 rounded-[4px] border border-rule bg-surface-2 px-2 py-[5px] text-[12.5px] text-ink"
              >
                <option value="">— ignora —</option>
                {csv.columns.map((c, i) => <option key={i} value={i}>{c || `colonna ${i + 1}`}</option>)}
              </select>
            </label>
          ))}
        </div>
      </div>

      {dupColumns.length > 0 ? (
        <div className="rounded-[5px] border border-warn/40 bg-warn/10 p-[12px_16px] text-[12.5px] text-ink">
          <AlertTriangle className="mr-1.5 inline size-4 text-warn" />
          Stessa colonna usata da più campi: {dupColumns.map((labels) => labels.join(" + ")).join(" · ")}. Ogni campo leggerà lo stesso valore.
        </div>
      ) : null}

      {/* defaults */}
      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
          <h3 className="text-[13px] font-semibold">Valori predefiniti</h3>
          <span className="micro ml-auto">usati se la colonna manca</span>
        </div>
        <div className="flex flex-wrap gap-6 p-[16px_18px] text-[13px]">
          <label className="flex items-center gap-3">
            <span className="text-ink-soft">Contratto</span>
            <select value={defaults.contract} onChange={(e) => setDefaults((d) => ({ ...d, contract: e.target.value as Defaults["contract"] }))} className="rounded-[4px] border border-rule bg-surface-2 px-2 py-[5px] text-[12.5px] text-ink">
              {CONTRACT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-3">
            <span className="text-ink-soft">Tipo</span>
            <select value={defaults.kind} onChange={(e) => setDefaults((d) => ({ ...d, kind: e.target.value as Defaults["kind"] }))} className="rounded-[4px] border border-rule bg-surface-2 px-2 py-[5px] text-[12.5px] text-ink">
              <option value="immobile">immobile</option>
              <option value="cantiere">cantiere</option>
            </select>
          </label>
        </div>
      </div>

      {/* preview */}
      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
          <h3 className="text-[13px] font-semibold">Anteprima</h3>
          <span className="micro ml-auto">prime {Math.min(previews.length, 12)} righe</span>
        </div>
        <ul className="max-h-[360px] divide-y divide-rule-soft overflow-y-auto">
          {previews.slice(0, 12).map((p, i) => (
            <li key={i} className="flex items-center gap-3 p-[9px_16px] text-[12.5px]">
              <span className="font-mono text-[11px] text-ink-mute">riga {records[i]?.line}</span>
              <RowBadge level={p.level} />
              <span className="font-medium text-ink">{p.title || "(senza titolo)"}</span>
              {p.slug ? <span className="font-mono text-[11px] text-ink-mute">/{p.slug}</span> : null}
              {p.notes.length ? <span className="truncate text-ink-soft">· {p.notes.join(" · ")}</span> : null}
            </li>
          ))}
        </ul>
        <p className="micro border-t border-rule p-[10px_16px]">Gli slug duplicati ricevono un suffisso (-2, -3); gli immobili già esistenti vengono saltati.</p>
      </div>

      {/* action bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={doImport}
          disabled={!canDerive || okCount === 0 || commit.isPending}
          className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-[10px] text-[13px] font-semibold text-accent-on hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {commit.isPending ? "Importazione…" : <>Importa {okCount} immobili <ArrowRight className="size-4" /></>}
        </button>
        <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-[4px] border border-rule bg-surface-2 px-3 py-2 text-[13px] text-ink-soft hover:border-accent hover:text-ink">
          <RotateCcw className="size-4" /> Annulla
        </button>
        {!canDerive ? <span className="text-[12.5px] text-warn">Mappa almeno <strong>Titolo</strong>, <strong>Slug</strong> o <strong>Città</strong> per generare gli URL.</span> : null}
        {errCount > 0 ? <span className="text-[12.5px] text-ink-soft">{errCount} righe con errori sono escluse.</span> : null}
        {capped ? <span className="text-[12.5px] text-warn">max {MAX_ROWS} per import · le altre {eligibleCount - MAX_ROWS} caricale in un secondo file.</span> : null}
        {commit.isError ? <span className="text-[12.5px] text-risk">Errore: {(commit.error as Error).message}</span> : null}
      </div>
    </div>
  );
}

function Header(): JSX.Element {
  return (
    <header>
      <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Importa immobili</h1>
      <p className="mt-[5px] text-[13.5px] text-ink-soft">
        Carica un CSV dal tuo vecchio gestionale o portale. Gli immobili entrano come <strong>bozze</strong>: li rivedi e pubblichi dalla scheda.
      </p>
    </header>
  );
}

function Stat({ v, l, last }: { v: string; l: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}</div>
      <div className="micro mt-[5px]">{l}</div>
    </div>
  );
}

function RowBadge({ level }: { level: RowLevel }): JSX.Element {
  if (level === "ok") return <CheckCircle2 className="size-[15px] shrink-0 text-success" aria-label="pronto" role="img" />;
  if (level === "warn") return <AlertTriangle className="size-[15px] shrink-0 text-warn" aria-label="attenzione" role="img" />;
  return <XCircle className="size-[15px] shrink-0 text-risk" aria-label="errore" role="img" />;
}
